import { differenceInHours, formatISO, formatISO9075, isValid } from 'date-fns';
import { useCallback, useMemo } from 'react';
import { createIntl, useIntl } from 'react-intl';

import { formatDate as formatDateEds } from '~/eds/src/utils/formatDate';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';

import { getFormattingLocale, parseUserDate } from '../../utils';
import {
  DateFormat,
  getReactIntlDateFormatOptions,
  getRelativeTimeOptions,
} from './utils';

export const useFormatDate = () => {
  const intl = useIntl();
  const locale = getFormattingLocale();
  const overrideIntlLocale = useMemo(
    () => createIntl({ locale, messages: {} }),
    [locale],
  );
  const hasLocalization = useFlag(FlagType.LanguageSelect);

  const formatProseDate = (date: Date) => {
    const day = date.getDate();
    const month = overrideIntlLocale.formatDate(date, { month: 'long' });
    const year = overrideIntlLocale.formatDate(date, { year: 'numeric' });

    return intl.formatMessage(
      {
        id: 'useFormatDate.proseFormat',
        defaultMessage: '{day} day of {month}, {year}',
        description:
          'Formatting dates in the prose format. Example: 1 day of January, 2025',
      },
      { day, month, year },
    );
  };

  const formatRelativeDate = (date: Date) => {
    const { value, unit } = getRelativeTimeOptions(date);
    return overrideIntlLocale.formatRelativeTime(value, unit, {
      numeric: 'auto',
    });
  };

  const formatDurationDate = (date: Date) => {
    const { value, unit } = getRelativeTimeOptions(date);

    if (differenceInHours(new Date(), date) >= 35) {
      return overrideIntlLocale.formatDate(date, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    }

    if (value === 0 || (unit === 'second' && Math.abs(value) <= 5)) {
      return overrideIntlLocale.formatRelativeTime(value, unit, {
        numeric: 'auto',
      });
    }

    return overrideIntlLocale.formatRelativeTime(value, unit, {
      numeric: 'always',
      style: 'long',
    });
  };

  /**
   *
   * ISO Standards → International technical specifications (Universal format for APIs, databases, system integration)
   * ICU Standards → Industry standard library for international user experience
   * technical formats follow ISO standards for machine interoperability
   * user formats follow ICU standards for human experience.
   * Universally accepted technical format" is exactly what you want for APIs, databases, and system integration
   */
  return useCallback(
    (
      date: Date | string | number | null | undefined,
      format: DateFormat = 'iso',
    ) => {
      if (!date) return '—'; // Treats 0, null, undefined, '' as "no date"

      const parsedDate = parseUserDate(date);

      // Validate parsed date
      if (!isValid(parsedDate) || !parsedDate) {
        return '—';
      }

      //@ts-ignore fixed_long is not a valid format for useFormatDate, but it is for formatDateEds
      if (format === 'fixed_long') {
        return formatDateEds(parsedDate, format);
      }

      if (format === 'relative') {
        return hasLocalization
          ? formatRelativeDate(parsedDate)
          : formatDateEds(parsedDate, format);
      }

      if (format === 'duration') {
        return hasLocalization
          ? formatDurationDate(parsedDate)
          : formatDateEds(parsedDate, format);
      }

      if (format === 'utc') {
        //2025-02-25T11:41:11Z
        return hasLocalization
          ? parsedDate.toISOString().split('.')[0] + 'Z'
          : formatDateEds(parsedDate, format);
      }

      //The below formats (iso, iso_date, iso_datetime, iso_complete) are affected by the timezone, not locale,
      //For technical use only. e.g for API values

      if (format === 'iso_date' || format === 'iso') {
        //2025-02-25
        return hasLocalization
          ? formatISO(parsedDate, { representation: 'date' })
          : formatDateEds(parsedDate, format);
      }

      if (format === 'iso_datetime') {
        //2025-02-25 hh:mm:ss
        return hasLocalization
          ? formatISO9075(parsedDate)
          : formatDateEds(parsedDate, format);
      }

      if (format === 'iso_complete') {
        //2025-02-25T11:41:11-05:00
        return hasLocalization
          ? formatISO(parsedDate, {
              representation: 'complete',
            })
          : formatDateEds(parsedDate, format);
      }

      //TODO: do not prose format. Supported just for workflow builder, not supported by react intl
      if (format === 'prose') {
        return hasLocalization
          ? formatProseDate(parsedDate)
          : formatDateEds(parsedDate, format);
      }

      //!TODO: reworks to handle eds formats differently than react intl
      if (format === 'short_datetime' || format === 'input_date') {
        const getFormatOptions = (format: DateFormat) => {
          //With EDS a format of'short_datetime' refers to full and with react intl 'full' refers to short_datetime

          if (format === 'short_datetime') {
            return hasLocalization ? ('short_datetime' as any) : 'full';
          } else if (format === 'input_date') {
            return hasLocalization ? ('input_date' as any) : ('iso' as any);
          }
        };

        const changedFormat = getFormatOptions(format);

        return hasLocalization
          ? overrideIntlLocale.formatDate(
              parsedDate,
              getReactIntlDateFormatOptions(format),
            )
          : formatDateEds(parsedDate, changedFormat);
      }

      return hasLocalization
        ? overrideIntlLocale.formatDate(
            parsedDate,
            getReactIntlDateFormatOptions(format),
          )
        : formatDateEds(parsedDate, format);
    },
    [intl, overrideIntlLocale, hasLocalization],
  );
};
